/**
 * 지점 허브의 "지금 열려 있는 것"을 URL 파라미터로 나른다.
 *
 * 상품 상세로 갔다가 브라우저 뒤로가기로 돌아오면 같은 지점의 차종 목록이 그대로
 * 다시 열려야 한다. 시트 전환은 히스토리를 쌓지 않고(`replaceState`) 현재 항목의
 * URL만 갱신하므로, 허브 자체의 "뒤로"와 브라우저 뒤로가기가 서로 싸우지 않는다.
 *
 * 이 파라미터들은 검색 전이 모듈이 뜻을 해석하지 않고 그대로 나른다
 * (`search-state.ts` 의 PASSTHROUGH_KEYS).
 */

import {
  parseBikeType,
  parseRideStyles,
  parseTransmission,
  type BikeType,
  type RideStyle,
  type Transmission,
} from "./bike-facets";
import { parseBikeMaker, type BikeMaker } from "./bike-makers";
import { parsePartySizes, type PartySize } from "./camp-party";
import type { HubKind } from "./station-hub";

export const HUB_PARAM_KEYS = [
  "hub",
  "station",
  "model",
  "bclass",
  "maker",
  "btype",
  "ride",
  "trans",
  "party",
  "lm",
  "sheet",
] as const;
export type HubParamKey = (typeof HUB_PARAM_KEYS)[number];

export type HubUrlMode = "stations" | "catalog";

/**
 * 허브 종류별 기본 탭 — 기본 탭은 URL 에 `hub` 를 남기지 않는다.
 * 오토바이·캠핑 모두 상품 쪽이 먼저(오토바이 10-06 "무슨 바이크"부터, 캠핑 10-15 장비부터).
 *   - mobility: 기본 = 차종(키 없음), 지점 = `hub=stations`. 예전 `hub=catalog` 링크도 차종.
 *   - camping : 기본 = 장비(키 없음), 캠핑장 = `hub=stations`. 예전 `hub=catalog` 링크도 장비.
 * `kind` 는 앞으로 종류별로 갈릴 때를 위해 남긴다.
 */
export function defaultHubMode(_kind: HubKind): HubUrlMode {
  return "catalog";
}

/** `hub` 값 → 탭. 빈 값·알 수 없는 값은 종류의 기본 탭으로 떨어진다. */
function parseHubMode(value: string | null, kind: HubKind): HubUrlMode {
  if (value === "catalog" || value === "stations") return value;
  return defaultHubMode(kind);
}

export interface HubUrlState {
  mode: HubUrlMode;
  /** 지점 모드에서 열린 지점(차종 목록) */
  stationId?: string;
  /** 차종 모드에서 열린 모델 */
  modelKey?: string;
  /** 선택된 배기량 클래스(`bclass`) — "전체"면 없음 */
  classKey?: string;
  /** 제조사 다중 선택(`maker`, 쉼표 목록) — 비면 없음. 알려진 키만 남는다. */
  makers?: BikeMaker[];
  /** 타입 다중 선택(`btype`, 쉼표 목록) — 비면 없음. 알려진 키만 남는다. */
  types?: BikeType[];
  /** 라이딩 다중 선택(`ride`, 쉼표 목록) — 비면 없음. */
  rides?: RideStyle[];
  /** 변속 단일 선택(`trans`, AT|MT) — 없으면 전체. */
  trans?: Transmission;
  /** 캠핑 인원 다중 선택(`party`, 쉼표 목록 "2,3") — 비면 없음. 1~4 만 남는다. */
  parties?: PartySize[];
  /** 고른 명소(`lm`, 위키데이터 QID) — 거리 기준점이 된다. 없으면 기본 기준점. */
  landmarkId?: string;
  /** 모바일 드로어가 전체(100%)로 펼쳐져 있는가 — 낮은 스냅이면 없음 */
  expanded?: boolean;
}

type ParamSource = { get(key: string): string | null };

const clean = (value: string | null): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/** 쉼표 목록 → 조각 배열(공백 제거·빈 조각 제외). */
const splitList = (value: string | null): string[] =>
  (value ?? "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

/** 알려진 타입 키만, 중복 제거·입력 순서 유지. 비면 undefined(키를 URL 에서 지운다). */
function parseTypes(value: string | null): BikeType[] | undefined {
  const out: BikeType[] = [];
  for (const part of splitList(value)) {
    const key = parseBikeType(part);
    if (key && !out.includes(key)) out.push(key);
  }
  return out.length > 0 ? out : undefined;
}

/** 알려진 제조사 키만(소문자), 중복 제거·입력 순서 유지. 비면 undefined. */
function parseMakers(value: string | null): BikeMaker[] | undefined {
  const out: BikeMaker[] = [];
  for (const part of splitList(value)) {
    const key = parseBikeMaker(part);
    if (key && !out.includes(key)) out.push(key);
  }
  return out.length > 0 ? out : undefined;
}

/** 알려진 인원(1~4)만, 중복 제거·입력 순서 유지. 비면 undefined. */
function parseParties(value: string | null): PartySize[] | undefined {
  const out = parsePartySizes(splitList(value));
  return out.length > 0 ? out : undefined;
}

function parseRides(value: string | null): RideStyle[] | undefined {
  const out = parseRideStyles(splitList(value));
  return out.length > 0 ? out : undefined;
}

/** URL → 허브 상태. 알 수 없는 값은 종류의 기본 탭(defaultHubMode)으로 떨어진다. */
export function parseHubParams(params: ParamSource, kind: HubKind): HubUrlState {
  const hub = params.get("hub");
  // 예전 캠핑 링크(기본 탭이 캠핑장이던 10-15 이전)는 캠핑장 시트를 `hub` 없이 `station=` 으로만
  // 남겼다 — 캠핑에서 `hub` 가 비어 있고 지점이 있으면 지점 탭으로 읽어 그 시트를 그대로 연다.
  // 오토바이는 지점 탭이 늘 `hub=stations` 였으므로 해당 없다.
  const legacyCampingStation =
    kind === "camping" && !clean(hub) && !!clean(params.get("station"));
  const mode = legacyCampingStation ? "stations" : parseHubMode(hub, kind);
  return {
    mode,
    stationId: mode === "stations" ? clean(params.get("station")) : undefined,
    modelKey: mode === "catalog" ? clean(params.get("model")) : undefined,
    classKey: clean(params.get("bclass")),
    makers: parseMakers(params.get("maker")),
    types: parseTypes(params.get("btype")),
    rides: parseRides(params.get("ride")),
    trans: parseTransmission(params.get("trans")) ?? undefined,
    parties: parseParties(params.get("party")),
    // 명소 id 는 형식을 거르지 않는다 — 전 세계 명소("wd:Q…", 09-28)·지명 사전 id(09-29)가
    // 모두 실린다. 예전 QID 전용 검사가 "wd:" 를 버려, 지점을 열어 주소가 바뀌면 명소가
    // 풀렸다(10-01). 풀 수 없는 값은 usePlacePoint 가 null 로 돌려준다.
    landmarkId: clean(params.get("lm")),
    expanded: params.get("sheet") === "full" ? true : undefined,
  };
}

/** 허브 상태 → 파라미터 값. undefined 인 키는 URL에서 지운다. */
export function hubParamValues(
  state: HubUrlState,
  kind: HubKind,
): Record<HubParamKey, string | undefined> {
  return {
    // 기본 탭은 키를 남기지 않는다 — 기본이 아닌 탭만 그 이름을 쓴다.
    hub: state.mode === defaultHubMode(kind) ? undefined : state.mode,
    station: state.mode === "stations" ? state.stationId : undefined,
    model: state.mode === "catalog" ? state.modelKey : undefined,
    bclass: state.classKey,
    maker: state.makers?.length ? state.makers.join(",") : undefined,
    btype: state.types?.length ? state.types.join(",") : undefined,
    ride: state.rides?.length ? state.rides.join(",") : undefined,
    trans: state.trans,
    party: state.parties?.length ? state.parties.join(",") : undefined,
    lm: state.landmarkId,
    sheet: state.expanded ? "full" : undefined,
  };
}

/**
 * 현재 쿼리스트링에 허브 파라미터만 덮어쓴다(다른 파라미터는 그대로).
 * 반환값은 `?` 를 포함하고, 비면 빈 문자열.
 */
export function applyHubParams(
  search: string,
  state: HubUrlState,
  kind: HubKind,
): string {
  const params = new URLSearchParams(search);
  const values = hubParamValues(state, kind);
  for (const key of HUB_PARAM_KEYS) {
    const value = values[key];
    if (value) params.set(key, value);
    else params.delete(key);
  }
  const query = params.toString();
  return query ? `?${query}` : "";
}

/** 상태 비교용 키 — 렌더 중 "URL이 바뀌었나"를 싸게 판정한다. */
export function hubStateKey(state: HubUrlState): string {
  return [
    state.mode,
    state.stationId ?? "",
    state.modelKey ?? "",
    state.classKey ?? "",
    state.makers?.join(",") ?? "",
    state.types?.join(",") ?? "",
    state.rides?.join(",") ?? "",
    state.trans ?? "",
    state.parties?.join(",") ?? "",
    state.landmarkId ?? "",
    state.expanded ? "full" : "",
  ].join("|");
}

/** 시트 깊이 — 목록·카탈로그는 0, 지점/모델 시트는 1. */
export function hubDepth(state: HubUrlState): 0 | 1 {
  return state.stationId || state.modelKey ? 1 : 0;
}

/**
 * 이 전환이 새 히스토리 항목을 만들어야 하는가.
 *
 * "화면이 바뀌는" 전환(목록 → 지점 시트, 카탈로그 → 모델 시트, 지점 ↔ 차종 모드,
 * 다른 지점으로 갈아타기)만 push 다. 필터 축(제조사·배기량·타입·라이딩·변속·인원)·드로어 높이는
 * 화면이 아니라 상태라 replace — 그래야 네브바 뒤로가기가 필터 조작 이력을 하나씩
 * 되짚지 않는다.
 * 닫기(깊이 1 → 0)도 replace 다: 뒤로가기는 쌓인 항목을 되감아 처리한다.
 */
export function historyModeFor(
  prev: HubUrlState,
  next: HubUrlState,
): "push" | "replace" {
  if (prev.mode !== next.mode) return "push";
  const from = hubDepth(prev);
  const to = hubDepth(next);
  if (to > from) return "push";
  if (
    to === 1 &&
    from === 1 &&
    (prev.stationId !== next.stationId || prev.modelKey !== next.modelKey)
  ) {
    return "push";
  }
  return "replace";
}
